/* eslint-disable react/button-has-type */ // Type is given by callee
import React from 'react';

import classNames from 'classnames';

import Spinner from '@travauxlib/shared/src/components/DesignSystem/assets/Spinner.svg?react';
import type {
  BadgeSizes,
  BadgeVariant,
} from '@travauxlib/shared/src/components/DesignSystem/components/Badge/types';

import { ActionDisabledWithTooltip } from './ActionDisabledWithTooltip';

import { Badge } from '../Badge';

import type { Placement } from '@floating-ui/react';
import type { Argument } from 'classnames';

export type IconSizes = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type Variant =
  | 'white'
  | 'grey'
  | 'primary'
  | 'contextual'
  | 'info'
  | 'success'
  | 'error'
  | 'warning'
  | 'dark'
  | 'secondary'
  | 'ghost'
  | 'inverse';

const iconButtonClassesBySize: { [key in IconSizes]: Argument[] } = {
  xxs: ['size-sm'],
  xs: ['size-md'],
  sm: ['size-lg'],
  md: ['size-xl'],
  lg: ['size-xxl'],
  xl: ['size-xxl'],
};

const iconContainerClassesBySize: { [key in IconSizes]: Argument[] } = {
  xxs: ['size-sm', '[&>svg]:size-sm'],
  xs: ['size-md', '[&>svg]:size-md'],
  sm: ['size-md', '[&>svg]:size-md'],
  md: ['size-lg', '[&>svg]:size-lg'],
  lg: ['size-xl', '[&>svg]:size-xl'],
  xl: ['size-xl', '[&>svg]:size-xl'],
};

const iconButtonClassesDisabledByVariant: { [key in Variant]: Argument[] } = {
  white: ['icon-disabled'],
  grey: ['icon-disabled'],
  error: ['icon-danger-weak'],
  success: ['icon-success-weak'],
  warning: ['icon-warning-weak'],
  info: ['icon-info-weak'],
  contextual: ['icon-contextual-weak'],
  primary: ['icon-disabled'],
  dark: ['icon-disabled ctn-neutral-white! border-1!'],
  secondary: ['icon-disabled'],
  ghost: ['icon-disabled'],
  inverse: ['icon-disabled'],
};

const iconButtonClassesActiveByVariant: { [key in Variant]: Argument[] } = {
  white: ['icon-primary action-bg-neutral-weak-pressed'],
  grey: ['icon-primary ctn-neutral-grey-strong'],
  error: ['icon-danger-strong action-bg-danger-weak-pressed'],
  success: ['icon-success-strong action-bg-success-weak-pressed'],
  warning: ['icon-warning-strong action-bg-warning-weak-pressed'],
  info: ['icon-info-strong action-bg-info-weak-pressed'],
  contextual: ['icon-contextual-strong action-bg-neutral-weak-pressed'],
  primary: ['icon-brand action-bg-primary-weak-actived'],
  dark: ['icon-inverse ctn-neutral-grey-strong'],
  secondary: ['icon-blue action-bg-secondary-weak-actived'],
  ghost: ['icon-brand action-bg-neutral-weak-pressed'],
  inverse: ['icon-inverse bg-transparent'],
};

const iconButtonClassesByVariant: { [key in Variant]: Argument[] } = {
  white: [
    'icon-primary hover:icon-secondary focus-visible:icon-primary active:icon-primary disabled:icon-disabled',
    'bg-transparent hover:action-bg-neutral-weak-hover focus-visible:bg-transparent active:action-bg-neutral-weak-pressed disabled:bg-transparent',
  ],
  grey: [
    'icon-secondary hover:icon-primary focus-visible:icon-secondary active:icon-primary disabled:icon-disabled',
    'bg-transparent hover:action-bg-neutral-weak-hover focus-visible:bg-transparent active:ctn-neutral-grey-strong disabled:bg-transparent',
  ],
  error: [
    'icon-danger-strong hover:icon-danger-strong focus-visible:icon-danger-strong active:icon-danger-strong disabled:icon-disabled',
    'bg-transparent hover:action-bg-danger-weak-hover focus-visible:bg-transparent active:action-bg-danger-weak-pressed disabled:bg-transparent',
  ],
  success: [
    'icon-success-strong hover:icon-success-strong focus-visible:icon-success-strong active:icon-success-strong disabled:icon-disabled',
    'bg-transparent hover:action-bg-success-weak-hover focus-visible:bg-transparent active:action-bg-success-weak-pressed disabled:bg-transparent',
  ],
  warning: [
    'icon-warning-strong hover:icon-warning-strong focus-visible:icon-warning-strong active:icon-warning-strong disabled:icon-disabled',
    'bg-transparent hover:action-bg-warning-weak-hover focus-visible:bg-transparent active:action-bg-warning-weak-pressed disabled:bg-transparent',
  ],
  info: [
    'icon-info-strong hover:icon-info-strong focus-visible:icon-info-strong active:icon-info-strong disabled:icon-disabled',
    'bg-transparent hover:action-bg-info-weak-hover focus-visible:bg-transparent active:action-bg-info-weak-pressed disabled:bg-transparent',
  ],
  contextual: [
    'icon-contextual-strong hover:icon-contextual-strong focus-visible:icon-contextual-strong active:icon-contextual-strong disabled:icon-disabled',
    'bg-transparent hover:action-bg-neutral-weak-hover focus-visible:bg-transparent active:action-bg-neutral-weak-pressed disabled:bg-transparent',
  ],
  primary: [
    'icon-brand hover:icon-brand focus-visible:icon-brand active:icon-brand disabled:icon-disabled',
    'bg-transparent hover:action-bg-primary-weak-hover focus-visible:bg-transparent active:action-bg-primary-weak-pressed disabled:bg-transparent',
  ],
  dark: [
    'icon-primary',
    'ctn-neutral-white active:ctn-neutral-grey-strong',
    'border-neutral border-1!',
  ],
  secondary: [
    'icon-blue hover:icon-blue focus-visible:icon-blue active:icon-blue disabled:icon-disabled',
    'bg-transparent hover:action-bg-secondary-weak-hover focus-visible:bg-transparent active:action-bg-secondary-weak-pressed disabled:bg-transparent',
  ],
  ghost: [
    'icon-brand hover:icon-brand focus-visible:icon-brand active:icon-brand disabled:icon-disabled',
    'bg-transparent hover:action-bg-neutral-weak-hover focus-visible:action-bg-neutral-weak-pressed active:bg-transparent disabled:bg-transparent',
  ],
  inverse: [
    'icon-inverse hover:icon-inverse focus-visible:icon-inverse active:icon-inverse disabled:icon-disabled',
    'bg-transparent hover:bg-transparent focus-visible:bg-transparent active:bg-transparent disabled:bg-transparent',
  ],
};

const buttonCssReset = 'p-0';

const iconButtonClassesByState = {
  disabled: iconButtonClassesDisabledByVariant,
  active: iconButtonClassesActiveByVariant,
  default: iconButtonClassesByVariant,
};

const getIconButtonState = (
  disabled: boolean,
  isActive: boolean,
): keyof typeof iconButtonClassesByState => {
  if (disabled) {
    return 'disabled';
  }

  if (isActive) {
    return 'active';
  }

  return 'default';
};

export const makeIconButtonClassnames = ({
  size,
  variant,
  disabled = false,
  isActive = false,
}: {
  size: IconSizes;
  variant: Variant;
  disabled?: boolean;
  isActive?: boolean;
}): Argument[] => [
  buttonCssReset,
  'flex justify-center items-center',
  'rounded-full border-0',
  'focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus',
  ...iconButtonClassesByState[getIconButtonState(disabled, isActive)][variant],
  ...iconButtonClassesBySize[size],
  {
    'bg-transparent pointer-events-none': disabled,
  },
];

export const IconButtonContent: React.FC<{
  size: IconSizes;
  children: React.ReactNode;
}> = ({ children, size }) => (
  <span
    className={classNames(
      'flex justify-center items-center shrink-0',
      ...iconContainerClassesBySize[size],
    )}
  >
    {children}
  </span>
);

export type CustomIconButtonProps = {
  variant?: Variant;
  size?: IconSizes;
  disabled?: boolean;
  isActive?: boolean;
  badgeValue?: number;
  badgeVariant?: BadgeVariant;
  children?: React.ReactNode;
  disabledMessageTooltip?: string;
  positionTooltip?: Placement;
  dataTestId?: string;
  loading?: boolean;
};

export type IconButtonProps = CustomIconButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

const badgeSizeMapper: { [K in IconSizes]: BadgeSizes } = {
  xxs: 'xs',
  xs: 'xs',
  sm: 'xs',
  md: 'sm',
  lg: 'md',
  xl: 'md',
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      size = 'md',
      children,
      variant = 'white',
      className,
      badgeValue,
      disabledMessageTooltip,
      positionTooltip,
      badgeVariant = 'light',
      type = 'button',
      isActive,
      dataTestId,
      loading,
      ...rest
    },
    ref,
  ) => (
    <ActionDisabledWithTooltip
      disabled={rest.disabled}
      disabledMessage={disabledMessageTooltip}
      position={positionTooltip}
    >
      <button
        ref={ref}
        type={type}
        data-testid={dataTestId || 'icon-button-container'}
        className={classNames(
          makeIconButtonClassnames({
            size,
            variant,
            isActive: isActive,
            disabled: rest.disabled,
          }),
          className,
          { relative: badgeValue },
        )}
        {...rest}
      >
        {badgeValue ? (
          <div className="absolute left-1/2 bottom-1/2">
            <Badge size={badgeSizeMapper[size]} variant={badgeVariant} value={badgeValue} />
          </div>
        ) : null}
        <IconButtonContent size={size}>
          {loading ? <Spinner className="animate-spin" data-testid="loading-icon" /> : children}
        </IconButtonContent>
      </button>
    </ActionDisabledWithTooltip>
  ),
);
